import { buildWorkerSource } from './workerSource'

/**
 * Running the learner's JavaScript, in a Worker, with a hard kill.
 *
 * ── Why a Worker and not an iframe ──────────────────────────────────────────
 *
 * The obvious design is an iframe with sandbox="allow-scripts" (no
 * allow-same-origin), which gives an opaque origin — the strongest boundary
 * available in a browser without a server. That was the first plan. Two facts
 * killed it:
 *
 *   1. An iframe shares the main thread. `while (true) {}` inside one freezes
 *      the whole tab, and there is no way to interrupt it. Removing the iframe
 *      does not help, because the code removing it never gets to run.
 *   2. Creating a Worker from a blob: URL inside an opaque-origin iframe is
 *      blocked by browsers, so you cannot nest one inside the other to get both
 *      properties.
 *
 * Termination is not negotiable. The single most common thing a learner does
 * in a loop lesson is write an infinite loop, and a teaching tool that requires
 * a tab restart when that happens is worse than no tool. So: a Worker on this
 * origin, where `terminate()` genuinely stops a spinning loop, and the origin
 * boundary is replaced by removing the capabilities that make an origin worth
 * reaching.
 *
 * ── What that actually protects, stated honestly ────────────────────────────
 *
 * A dedicated Worker has no DOM and no `document.cookie`, so the session cookie
 * is already out of reach — and it is HttpOnly besides. The bootstrap then
 * deletes every I/O primitive a Worker has: fetch, XMLHttpRequest, WebSocket,
 * EventSource, importScripts, and Worker itself (so nested workers cannot be
 * used to obtain a fresh global with those intact). What remains is arithmetic.
 *
 * This is NOT a jail, and the lesson says so in those words. Someone determined
 * to escape a browser sandbox is not stopped by deleting globals. It is
 * adequate because of who is running the code: the learner, in their own
 * browser, against their own account. There is nothing here to steal that they
 * do not already have. Claiming more than that would be the kind of security
 * theatre Module 8 spends six lessons arguing against.
 */

export interface RunResult {
  /** Everything the code logged, in order. */
  output: string[]
  /** Set when the code threw, or when it was killed for running too long. */
  error: string | null
  timedOut: boolean
  durationMs: number
}

/** Long enough for any teaching snippet; short enough that a hang feels like a
 *  result rather than a broken page. */
const TIMEOUT_MS = 2000

let cachedUrl: string | null = null

function sandboxUrl(): string {
  if (!cachedUrl) {
    cachedUrl = URL.createObjectURL(new Blob([buildWorkerSource()], { type: 'text/javascript' }))
  }
  return cachedUrl
}

/**
 * Run a snippet. Always resolves — a thrown error and a timeout are both
 * results, not exceptions. The caller renders them; nothing here decides
 * whether the learner was right, because nothing here is allowed to.
 */
export function runJs(source: string): Promise<RunResult> {
  return new Promise((resolve) => {
    const started = Date.now()
    let worker: Worker
    try {
      worker = new Worker(sandboxUrl())
    } catch (e) {
      resolve({
        output: [],
        error: `The sandbox could not start: ${(e as Error).message}`,
        timedOut: false,
        durationMs: 0,
      })
      return
    }

    let settled = false
    const finish = (result: RunResult) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      worker.terminate()
      resolve(result)
    }

    // The whole reason this is a Worker. terminate() stops a synchronous
    // infinite loop dead; no try/catch, timeout, or flag check can.
    const timer = setTimeout(() => {
      finish({
        output: [],
        error: `Timed out after ${TIMEOUT_MS / 1000}s — is there a loop that never ends?`,
        timedOut: true,
        durationMs: Date.now() - started,
      })
    }, TIMEOUT_MS)

    worker.onmessage = (event: MessageEvent<{ output: string[]; error: string | null }>) => {
      finish({
        output: event.data.output ?? [],
        error: event.data.error ?? null,
        timedOut: false,
        durationMs: Date.now() - started,
      })
    }

    worker.onerror = (event) => {
      finish({
        output: [],
        error: event.message || 'The sandbox failed to run that.',
        timedOut: false,
        durationMs: Date.now() - started,
      })
    }

    worker.postMessage({ source })
  })
}
